import {useEffect, useLayoutEffect, useRef, useState} from "react";
import VehicleListItem from "../fragments/VehicleListItem";
import Filter from "../Filter";
import {
    LISTING_ALL_PAGE_TYPE,
    LISTING_ORDER_PAGE_TYPE,
    LISTING_MOTO_PAGE_TYPE,
    paginationPageItems,
} from "../Constants";
import SectionHeading from "../SectionHeading";
import InfiniteScroll from "react-infinite-scroll-component";
import {LinearProgress, ThemeProvider} from "@mui/material";
import {theme} from "../MuiCustomization";
import {Link, useParams, useSearchParams} from "react-router-dom";
import ListingActionBlock from "../ListingActionBlock";
import VehicleView from "./VehicleView";
import {setPageMeta} from "../Util";

const ALL_LISTING_VEHICLE_CATEGORIES = ["CHN_ORDER", "AUTO", "COMMERCIAL"];
const ORDER_LISTING_VEHICLE_CATEGORIES = ["CHN_ORDER"];
const MOTO_LISTING_VEHICLE_CATEGORIES = ["MOTO"];
const RESERVED_SEARCH_PARAMS = new Set(["sortBy", "vehicleCategory", "index", "itemsPerPageCount"]);

export default function VehicleList({pageType, currencyExchangeRate}) {
    // Anchor to scroll to top reliably
    const topRef = useRef(null);
    // Stable AbortController across renders
    const controllerRef = useRef(null);
    const requestIdRef = useRef(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const {articleId, brand} = useParams();
    const routeBrand = normalizeRouteBrand(brand);

    // 1 Lazy initialize from URL
    const [selectedFilters, setSelectedFilters] = useState(() =>
        parseSelectedFilters(searchParams, routeBrand)
    );
    const [sortBy, setSortBy] = useState(() => {
        const s = searchParams.get("sortBy");
        return s ? s : "INPUT";
    });

    const [vehicles, setVehicles] = useState(null);
    const [index, setIndex] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [relatedBrands, setRelatedBrands] = useState([]);
    const selectedFiltersSignature = JSON.stringify(selectedFilters);

    useEffect(() => {
        const urlSelectedFilters = parseSelectedFilters(searchParams, routeBrand);
        setSelectedFilters((currentSelectedFilters) =>
            JSON.stringify(urlSelectedFilters) !== JSON.stringify(currentSelectedFilters)
                ? urlSelectedFilters
                : currentSelectedFilters
        );

        const urlSortBy = searchParams.get("sortBy") || "INPUT";
        setSortBy((currentSortBy) =>
            urlSortBy !== currentSortBy ? urlSortBy : currentSortBy
        );
    }, [routeBrand, searchParams]);

    useEffect(() => {
        if (routeBrand) {
            setPageMeta(buildBrandPageMeta(routeBrand, pageType));
        }
    }, [pageType, routeBrand]);

    useEffect(() => {
        const controller = new AbortController();
        const queryParams = new URLSearchParams();
        if (pageType === LISTING_MOTO_PAGE_TYPE) {
            queryParams.append("vehicleCategory", "MOTO");
        } else if (pageType === LISTING_ORDER_PAGE_TYPE) {
            queryParams.append("vehicleCategory", "CHN_ORDER");
        } else {
            queryParams.append("vehicleCategory", "AUTO");
            queryParams.append("vehicleCategory", "COMMERCIAL");
        }

        fetch(`/api/brands?${queryParams.toString()}`, {signal: controller.signal})
            .then((response) => response.json())
            .then((data) => {
                const brands = Array.isArray(data) ? data : [];
                setRelatedBrands(
                    brands
                        .map((item) => ({
                            name: item?.name || "",
                            count: item?.count || 0
                        }))
                        .filter((item) => item.name && (!routeBrand || item.name.toLowerCase() !== routeBrand.toLowerCase()))
                        .sort((left, right) => {
                            if (right.count !== left.count) {
                                return right.count - left.count;
                            }
                            return left.name.localeCompare(right.name, "ru");
                        })
                        .slice(0, 12)
                );
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    setRelatedBrands([]);
                }
            });

        return () => controller.abort();
    }, [pageType, routeBrand]);

    // 3 On any change to pageType, sortBy, or filterBrands:
    //    • reset pagination
    //    • fetch once
    useEffect(() => {
        // cancel any in-flight request and create a new controller
        controllerRef.current?.abort();
        controllerRef.current = new AbortController();
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        resetPagination();
        // load the first page explicitly
        loadMoreArticles(0, requestId);

        return () => controllerRef.current?.abort();
    }, [pageType, sortBy, selectedFiltersSignature]);

    // Scroll to the very top before paint to avoid covered first items
    useLayoutEffect(() => {
        if (topRef.current) {
            try {
                topRef.current.scrollIntoView({block: "start", behavior: "instant"});
            } catch {
                topRef.current.scrollIntoView({block: "start", behavior: "auto"});
            }
        }
        window.scrollTo(0, 0);
    }, [pageType, sortBy, selectedFiltersSignature]);

    const loadMoreArticles = (page = index, requestId = requestIdRef.current) => {
        getArticleList(page).then((newItems) => {
            if (requestId !== requestIdRef.current) {
                return;
            }
            setVehicles((prev) => {
                // if prev is null (first load) treat it as []
                const base = prev || [];
                return [...base, ...newItems];
            });
            setIndex(page + 1);
            setHasMore(newItems.length > 0);
        }).catch((error) => {
            if (error.name !== "AbortError") {
                throw error;
            }
        });
    };

    function getArticleList(page) {
        const queryParams = new URLSearchParams();
        queryParams.set("index", page);
        queryParams.set("sortBy", sortBy);
        queryParams.set("itemsPerPageCount", paginationPageItems);

        if (pageType === LISTING_ORDER_PAGE_TYPE) {
            queryParams.set("vehicleCategory", "CHN_ORDER");
        } else if (pageType === LISTING_ALL_PAGE_TYPE) {
            queryParams.set("vehicleCategory", "CHN_ORDER,AUTO,COMMERCIAL");
        } else if (pageType === LISTING_MOTO_PAGE_TYPE) {
            queryParams.set("vehicleCategory", "MOTO");
        }

        appendSelectedFiltersToQueryParams(queryParams, selectedFilters);

        return fetch(`/api/cars?${queryParams.toString()}`, {signal: controllerRef.current.signal})
            .then((res) => res.json())
            .then((data) => data);
    }

    function handleSelectedFiltersUpdate(nextSelectedFilters) {
        const nextFilters = routeBrand
            ? {...nextSelectedFilters, brand: [routeBrand]}
            : nextSelectedFilters;
        setSelectedFilters(nextFilters);
        updateListingSearchParams(nextFilters, sortBy);
    }

    function handleSortByUpdate(sort) {
        setSortBy(sort);
        updateListingSearchParams(selectedFilters, sort);
    }

    function resetPagination() {
        setIndex(0);
        setHasMore(true);
        setVehicles(null);
    }

    function updateListingSearchParams(filters, sort) {
        const nextSearchParams = new URLSearchParams(searchParams);
        if (sort) {
            nextSearchParams.set("sortBy", sort);
        } else {
            nextSearchParams.delete("sortBy");
        }

        [...nextSearchParams.keys()].forEach((key) => {
            if (!RESERVED_SEARCH_PARAMS.has(key)) {
                nextSearchParams.delete(key);
            }
        });

        Object.entries(filters).forEach(([facetKey, values]) => {
            if (routeBrand && facetKey === "brand") {
                return;
            }
            values.forEach((value) => nextSearchParams.append(facetKey, value));
        });

        setSearchParams(nextSearchParams, {replace: true});
    }

    function parseSelectedFilters(params, selectedRouteBrand) {
        const parsedFilters = {};

        params.forEach((value, key) => {
            if (RESERVED_SEARCH_PARAMS.has(key)) {
                return;
            }

            const facetKey = key === "brands" ? "brand" : key;
            const values = key === "brands"
                ? value.split(",")
                : params.getAll(key);

            if (!parsedFilters[facetKey]) {
                parsedFilters[facetKey] = [];
            }

            values
                .map((facetValue) => facetValue.trim())
                .filter(Boolean)
                .forEach((facetValue) => {
                    if (!parsedFilters[facetKey].includes(facetValue)) {
                        parsedFilters[facetKey].push(facetValue);
                    }
                });
        });

        if (selectedRouteBrand) {
            parsedFilters.brand = [selectedRouteBrand];
        }

        Object.keys(parsedFilters).forEach((facetKey) => {
            parsedFilters[facetKey].sort((leftValue, rightValue) =>
                leftValue.localeCompare(rightValue, undefined, {sensitivity: "base"})
            );
        });

        return parsedFilters;
    }

    function appendSelectedFiltersToQueryParams(queryParams, filters) {
        Object.entries(filters).forEach(([facetKey, values]) => {
            values.forEach((value) => queryParams.append(facetKey, value));
        });
    }

    // If viewing a single vehicle, show the detail view
    if (articleId) {
        return (
            <VehicleView
                articleId={articleId}
                currencyExchangeRate={currencyExchangeRate}
                pageType={pageType}
            />
        );
    }

    function getSectionTitle(pageType) {
        if (routeBrand) {
            return routeBrand;
        }
        switch (pageType) {
            case LISTING_ORDER_PAGE_TYPE:
                return "Авто из Китая в нашем автосалоне";
            case LISTING_ALL_PAGE_TYPE:
                return "Авто в нашем автосалоне";
            case LISTING_MOTO_PAGE_TYPE:
                return "Мото в нашем автосалоне";
            default:
                return "";
        }
    }

    function getSectionMessage(pageType) {
        if (routeBrand) {
            const catalogHref = getCatalogHref(pageType);
            const catalogLabel = pageType === LISTING_MOTO_PAGE_TYPE
                ? "все бренды смотрите в каталоге мото"
                : "все бренды смотрите в каталоге авто";
            return (
                <>
                    Актуальные предложения {routeBrand} в каталоге и под заказ.
                    <br />
                    Откройте <Link to={catalogHref}>{catalogLabel}</Link>.
                </>
            );
        }
        switch (pageType) {
            case LISTING_ORDER_PAGE_TYPE:
                return (
                    <>
                        Автомобили в наличии и под заказ.
                    </>
                );
            case LISTING_MOTO_PAGE_TYPE:
                return "Мото в наличии и под заказ, новые и с пробегом.";
            case LISTING_ALL_PAGE_TYPE:
                return "Автомобили в наличии и под заказ, новые и с пробегом.";
            default:
                return "";
        }
    }

    function normalizeRouteBrand(value) {
        if (!value) {
            return "";
        }

        try {
            return decodeURIComponent(value).trim().replace(/\s+/g, " ");
        } catch {
            return value.trim().replace(/\s+/g, " ");
        }
    }

    function buildBrandPageMeta(routeBrandValue, currentPageType) {
        const pageTitle = currentPageType === LISTING_ORDER_PAGE_TYPE
            ? `${routeBrandValue} в каталоге авто под заказ | БелАвтоЭксперт`
            : currentPageType === LISTING_MOTO_PAGE_TYPE
                ? `${routeBrandValue} в каталоге мото | БелАвтоЭксперт`
                : `${routeBrandValue} в каталоге авто | БелАвтоЭксперт`;

        const pageDescription = currentPageType === LISTING_ORDER_PAGE_TYPE
            ? `Актуальные предложения ${routeBrandValue} в каталоге авто под заказ. Подбор, доставка, кредит и лизинг в БелАвтоЭксперт.`
            : currentPageType === LISTING_MOTO_PAGE_TYPE
                ? `Актуальные предложения ${routeBrandValue} в каталоге мото. Подбор, доставка и оформление в БелАвтоЭксперт.`
                : `Актуальные предложения ${routeBrandValue} в каталоге авто БелАвтоЭксперт.`;

        return {
            title: pageTitle,
            description: pageDescription,
            keywords: `${routeBrandValue}, каталог, БелАвтоЭксперт`,
            robots: "index, follow"
        };
    }

    function buildBrandPageHref(currentPageType, brandValue) {
        const encodedBrand = encodeURIComponent(brandValue);
        if (currentPageType === LISTING_ORDER_PAGE_TYPE) {
            return `/vehicleOrder/catalog/brand/${encodedBrand}`;
        }
        if (currentPageType === LISTING_MOTO_PAGE_TYPE) {
            return `/catalog-moto/brand/${encodedBrand}`;
        }
        return `/listing/brand/${encodedBrand}`;
    }

    function getCatalogHref(currentPageType) {
        if (currentPageType === LISTING_ORDER_PAGE_TYPE) {
            return "/vehicleOrder/catalog";
        }
        if (currentPageType === LISTING_MOTO_PAGE_TYPE) {
            return "/catalog-moto";
        }
        return "/listing";
    }

    // Otherwise show the listing + optional filter
    return (
        <div className="site-section">
            <div className="container">
                {/* Scroll anchor */}
                <div ref={topRef} aria-hidden="true" />

                <SectionHeading
                    sectionHeadingTitle={getSectionTitle(pageType)}
                    sectionHeadingMessage={getSectionMessage(pageType)}
                />

                {relatedBrands.length > 0 && (
                    <div className="mb-4 d-flex flex-wrap align-items-center gap-2">
                        <span className="text-muted small">
                            {routeBrand ? "Смотрите также:" : "Популярные бренды:"}
                        </span>
                        {relatedBrands.map((brandItem) => (
                            <Link
                                key={brandItem.name}
                                className="text-decoration-none small"
                                to={buildBrandPageHref(pageType, brandItem.name)}
                            >
                                {brandItem.name}
                            </Link>
                        ))}
                    </div>
                )}

                {(pageType === LISTING_ALL_PAGE_TYPE
                    || pageType === LISTING_MOTO_PAGE_TYPE
                    || pageType === LISTING_ORDER_PAGE_TYPE) && (
                    <Filter selectedFilters={selectedFilters}
                            onUpdateSelectedFilters={handleSelectedFiltersUpdate}
                            disabledFacetKeys={routeBrand ? ["brand"] : []}
                            hiddenFacetKeys={routeBrand ? ["brand"] : []}
                            vehicleCategories={
                                pageType === LISTING_MOTO_PAGE_TYPE
                                    ? MOTO_LISTING_VEHICLE_CATEGORIES
                                    : pageType === LISTING_ORDER_PAGE_TYPE
                                        ? ORDER_LISTING_VEHICLE_CATEGORIES
                                        : ALL_LISTING_VEHICLE_CATEGORIES
                            }/>
                )}

                {vehicles === null ? (
                    <ThemeProvider theme={theme}>
                        <LinearProgress color="secondary" />
                    </ThemeProvider>
                ) : vehicles.length > 0 ? (
                    <>
                        <ListingActionBlock
                            handleSortByUpdate={handleSortByUpdate}
                            sortBy={sortBy}
                        />
                        <InfiniteScroll
                            dataLength={vehicles.length}
                            next={() => loadMoreArticles()}
                            hasMore={hasMore}
                            loader={
                                <ThemeProvider theme={theme}>
                                    <LinearProgress color="secondary" />
                                </ThemeProvider>
                            }
                        >
                            <div className="row mt-4 w-100">
                                {vehicles.map((vehicle) => (
                                    <VehicleListItem
                                        key={vehicle.id}
                                        vehicleObj={vehicle}
                                        currencyExchangeRate={currencyExchangeRate}
                                        pageType={pageType}
                                    />
                                ))}
                            </div>
                        </InfiniteScroll>
                    </>
                ) : (
                    <p>Нет позиций в каталоге для просмотра.</p>
                )}
            </div>
        </div>
    );
}
